import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import clsx from 'clsx';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { CircleTickIcon } from '../../icons/CircleTickIcon';
import { QuestionTheme } from '../../styles/mfsTheme';
import { RaterCard } from './RaterCard';
import { RaterCategory, RaterNominationDetails } from '../../interfaces/RaterNominationDetails';
import { RaterStyles } from '../../styles/rater';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

interface RaterListProps {
  raterDetails?: RaterNominationDetails;
  addButtonDisabled: boolean;
  newAddedRaterId?: string;
  onAddClick(roleId: number): (event: React.SyntheticEvent) => void;
  onRemoveClick(roleId: number, raterId: number, ratingId: number): (event: React.SyntheticEvent) => void;
}

export const RaterList = ({
  raterDetails,
  addButtonDisabled,
  newAddedRaterId = '',
  onAddClick,
  onRemoveClick,
}: RaterListProps): JSX.Element => {
  const classes = RaterStyles(QuestionTheme);
  const { t } = useTranslation();
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));

  const getMinIdealText = (rater: RaterCategory): JSX.Element => (
    <Typography
      variant="body1"
      component="h6"
      className={classes.raterCardSubTitle}
      aria-label={`${t('ideal')} ${rater.ideal}, ${t('minimum')} ${rater.min}`}>
      {`${t('ideal')} ${rater.ideal}, ${t('minimum')} ${rater.min}`}
    </Typography>
  );

  const getMinIdealTextStacked = (rater: RaterCategory): JSX.Element => (
    <Box>
      <Typography
        variant="body1"
        component="h6"
        className={classes.raterCardSubTitle}
        aria-label={`${t('ideal')} ${rater.ideal}`}>
        {`${t('ideal')} ${rater.ideal}`}
      </Typography>
      <Typography
        variant="body1"
        component="h6"
        className={classes.raterCardSubTitle}
        aria-label={`${t('minimum')} ${rater.min}`}>
        {`${t('minimum')} ${rater.min}`}
      </Typography>
    </Box>
  );

  return (
    <Box mb={3}>
      <Grid container spacing={3}>
        <Grid xs={12} item>
          {raterDetails &&
            raterDetails.page.raters.map((rater) => {
              return (
                <RaterCard key={rater.roleId}>
                  <List disablePadding aria-label={rater.roleName}>
                    <ListItem>
                      <Grid item xs={6}>
                        <ListItemText disableTypography secondary={!smUp ? getMinIdealTextStacked(rater) : ''}>
                          <Typography
                            variant="h6"
                            component="h4"
                            className={classes.raterCardTitle}
                            aria-label={`${rater.roleName} (${rater.count})`}>
                            {`${rater.roleName} (${rater.count})`}
                          </Typography>
                        </ListItemText>
                      </Grid>
                      {smUp && (
                        <Grid item xs={6}>
                          {getMinIdealText(rater)}
                        </Grid>
                      )}
                      <ListItemSecondaryAction>
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          disabled={addButtonDisabled}
                          onClick={onAddClick(rater.roleId)}
                          aria-label={`${t('addNewFeedbackProvider')} ${rater.roleName}`}>
                          <div>{t('add')}</div>
                        </Button>
                      </ListItemSecondaryAction>
                    </ListItem>

                    {rater.existing &&
                      rater.existing.map((existingRater) => {
                        return (
                          <React.Fragment key={existingRater.raterId}>
                            <Divider aria-hidden="true" />
                            <ListItem
                              className={
                                newAddedRaterId &&
                                (newAddedRaterId.toString() === existingRater.raterId.toString() ||
                                  newAddedRaterId.toString() === existingRater.email)
                                  ? classes.highlight
                                  : ''
                              }>
                              <ListItemIcon>
                                <Box pr={2} display="inline">
                                  <CircleTickIcon colorClass={classes.circleTickColor} />
                                </Box>
                                <Typography
                                  variant="body1"
                                  className={clsx(classes.textAlignLeft, classes.raterName)}
                                  aria-label={`${existingRater.firstName} ${existingRater.lastName}`}>
                                  {`${existingRater.firstName} ${existingRater.lastName}`}
                                </Typography>
                              </ListItemIcon>
                              <ListItemSecondaryAction>
                                <Button
                                  className={classes.raterActionButtons}
                                  onClick={onRemoveClick(rater.roleId, existingRater.raterId, existingRater.ratingId)}
                                  aria-label={`${t('remove')} ${existingRater.firstName} ${existingRater.lastName}`}>
                                  <div>{t('remove')}</div>
                                </Button>
                              </ListItemSecondaryAction>
                            </ListItem>
                          </React.Fragment>
                        );
                      })}
                  </List>
                </RaterCard>
              );
            })}
        </Grid>
      </Grid>
    </Box>
  );
};
